import { useMutation, type UseMutationResult } from '@tanstack/react-query';

import { ApiError, apiVoid } from '@/lib/apiClient';

/**
 * Tenant-user password reset (slice 17x).
 *
 * Mirrors the wire contract in `api/src/modules/admin/routes.ts`
 * (`POST /api/admin/users/:userId/password`): body `{ password }`, min 12 /
 * max 128, and a **204 with an empty body** on success — which is why this goes
 * through `apiVoid`, not `apiJson`. `apiJson` would call `res.json()` on the
 * empty body and turn a successful reset into a parse error.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * 🔴 THE PLAINTEXT NEVER LIVES HERE.
 * ─────────────────────────────────────────────────────────────────────────────
 * It is passed straight to the request and is never stored in this module. The
 * dialog that owns it clears it from component state the moment submit resolves
 * — the same discipline `NewStaffDialog` keeps.
 *
 * There is NO query to invalidate on success: the admin portal has no
 * tenant-user list to refresh (see the seam note in the dialog), so a reset
 * changes nothing this client is holding.
 */
export interface ResetPasswordBody {
  /** The TENANT user's id. Staff live in a different table and 404 here. */
  userId: string;
  /** Sent once. Never stored here; the caller clears it after submit. */
  password: string;
}

/**
 * The failures the UI turns into a sentence a person can act on. Anything else
 * is a generic "try again".
 */
export type ResetPasswordFailure = 'not_found' | 'invalid' | 'unknown';

export function classifyResetError(error: unknown): ResetPasswordFailure {
  if (error instanceof ApiError) {
    // 404 — no user with that id (a staff id lands here too: staff are a
    // different table with their own self-service change). Never 403 for a
    // missing user, by design, so a 404 is unambiguously "no such user".
    if (error.status === 404) return 'not_found';
    // 400 `{error:'invalid_body'}` — a password outside 12..128. The form's own
    // floor should catch this first; this is the belt to that suspenders.
    if (error.status === 400) return 'invalid';
  }
  return 'unknown';
}

export function useResetPassword(): UseMutationResult<void, Error, ResetPasswordBody> {
  return useMutation({
    mutationFn: ({ userId, password }: ResetPasswordBody) =>
      apiVoid(`/api/admin/users/${encodeURIComponent(userId)}/password`, {
        method: 'POST',
        body: JSON.stringify({ password }),
      }),
  });
}
